import { useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";
import Skeleton from "../Skeleton";
import "./_table.scss";

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGINATION = { pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE };

const SortIcon = ({ sortState }) => {
  if (sortState === "asc") return <ArrowUp size={13} className="ui-table__sort-icon" />;
  if (sortState === "desc") return <ArrowDown size={13} className="ui-table__sort-icon" />;
  return <ChevronsUpDown size={13} className="ui-table__sort-icon ui-table__sort-icon--idle" />;
};

const ColumnResizer = ({ header }) => {
  if (!header.column.getCanResize()) return null;

  const stop = (handler) => (event) => {
    event.stopPropagation();
    handler(event);
  };

  return (
    <div
      className={`ui-table__resizer ${header.column.getIsResizing() ? "ui-table__resizer--active" : ""}`}
      onMouseDown={stop(header.getResizeHandler())}
      onTouchStart={stop(header.getResizeHandler())}
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={stop(() => header.column.resetSize())}
    />
  );
};

const DEFAULT_MIN_SIZE = 80;
// Sort icon (13px) + the header-content flex gap (4px) in _table.scss.
const SORT_ICON_ALLOWANCE = 17;
// Header cell's own left+right padding (10px 16px in _table.scss).
const HEADER_PADDING_ALLOWANCE = 32;
// First column gets an extra 8px of left padding (_table.scss's
// &__header-cell:first-child) to line up with PageHeaderBar/.filter-container
// above the table.
const FIRST_COLUMN_PADDING_ALLOWANCE = 8;

/**
 * A column's own `size` is its starting width, not a resize/auto floor -
 * TanStack's own default minSize is an unusably-small 20px. Without a
 * proper floor, a column can be squeezed (by drag, or just by its `size`
 * being small) until its own header label clips - the content columns
 * would then be narrower than the header naming them, which reads as
 * broken regardless of how wide the actual row data is. So: measure each
 * header's own natural rendered width (same font/classes as the real
 * header, off-screen) and use that as the floor, falling back to
 * DEFAULT_MIN_SIZE for anything shorter or non-string (JSX) headers whose
 * width can't be measured this way.
 */
const useHeaderMinSizes = (columns) => {
  const measureRef = useRef(null);
  const [minSizes, setMinSizes] = useState({});

  useLayoutEffect(() => {
    const measureEl = measureRef.current;
    if (!measureEl) return;

    const next = {};
    columns.forEach((column, index) => {
      const id = column.id ?? column.accessorKey;
      if (!id || typeof column.header !== "string") return;

      measureEl.textContent = column.header;
      const textWidth = measureEl.getBoundingClientRect().width;
      const sortable = column.enableSorting !== false;
      const paddingAllowance = HEADER_PADDING_ALLOWANCE + (index === 0 ? FIRST_COLUMN_PADDING_ALLOWANCE : 0);
      next[id] = Math.ceil(textWidth) + paddingAllowance + (sortable ? SORT_ICON_ALLOWANCE : 0);
    });
    setMinSizes(next);
  }, [columns]);

  return [minSizes, measureRef];
};

/**
 * A column's data can be wider than its own header label (e.g. a formatted
 * date/time string like "4:07 AM, 09/09/2026" under a short "Last activity
 * on" header) - the header-only floor above doesn't protect that case, so a
 * column could still be sized/resized narrower than its data needs and clip
 * it via __cell's overflow:hidden/ellipsis. After each render of real (non-
 * skeleton) rows, measure every cell's actual scrollWidth - this reports the
 * content's true, un-clipped size even though the cell itself visually
 * clips via overflow:hidden - and use the widest cell per column as an
 * additional floor, so a column can't end up narrower than its own current
 * page's data.
 */
const useContentMinSizes = (tbodyRef, rows, isLoading) => {
  const [minSizes, setMinSizes] = useState({});

  useLayoutEffect(() => {
    const tbody = tbodyRef.current;
    if (!tbody || isLoading) return;

    const next = {};
    tbody.querySelectorAll("td[data-column-id]").forEach((td) => {
      const id = td.dataset.columnId;
      const width = td.scrollWidth;
      if (!next[id] || width > next[id]) next[id] = width;
    });
    setMinSizes(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, isLoading]);

  return minSizes;
};

// Matches the design's own per-cell skeleton width algorithm exactly
// (percentage of the cell's own width, cycling by row+column index) so a
// loading table looks like organically varied content instead of a
// uniform pixel-capped bar repeated in every cell.
const SKELETON_CELL_WIDTHS = ["78%", "55%", "62%", "40%", "70%", "48%", "85%", "36%"];

const SkeletonRows = ({ columns, rowCount }) => (
  <>
    {Array.from({ length: rowCount }).map((_, rowIndex) => (
      <tr key={`skeleton-${rowIndex}`} className="ui-table__row ui-table__row--skeleton">
        {columns.map((column, colIndex) => (
          <td
            key={column.id ?? colIndex}
            className={`ui-table__cell ${column.meta?.align === "right" ? "ui-table__cell--right" : ""}`.trim()}
          >
            <Skeleton
              className="ui-table__skeleton-bar"
              width={SKELETON_CELL_WIDTHS[(rowIndex + colIndex) % SKELETON_CELL_WIDTHS.length]}
            />
          </td>
        ))}
        <td className="ui-table__cell ui-table__cell--spacer" aria-hidden="true" />
      </tr>
    ))}
  </>
);

/**
 * Headless data grid built directly on @tanstack/react-table - the same
 * engine @simplifyhq/simplify-ui-reusables' own Table already wraps
 * internally (it's a direct dependency of that package), so existing
 * column defs across the app (accessorKey/accessorFn/cell/sortingFn/size)
 * need zero changes to work here. This owns rendering itself instead of
 * going through the reusables package, per the plan to retire it.
 *
 * Pagination is server-driven (manualPagination) - the caller owns
 * pagination/onPaginationChange and refetches on change. Sorting is
 * client-side over the current page's rows, matching how column
 * `sortingFn`s were already being used before this rewrite (there was
 * never a sorting prop wired to a server request anywhere in the app).
 *
 * Column widths are read through CSS custom properties (--header-x-size /
 * --col-x-size), not inline style={{width}} per cell, per TanStack's own
 * performant-resizing guidance: recomputing every cell's inline width on
 * every pixel of drag movement is what makes column resizing feel janky.
 * Cells just reference the var - only the var map itself (columnSizeVars)
 * recalculates while dragging, and only header/table-level state re-renders.
 */
const Table = ({
  columns,
  data,
  totalCount = 0,
  pagination = DEFAULT_PAGINATION,
  onPaginationChange,
  isLoading = false,
  emptyState,
  // A failed fetch and a genuinely-empty result both leave rows.length===0,
  // but they're not the same thing - `isError`/`errorState` let a caller
  // say "we couldn't load this" instead of it silently rendering as
  // `emptyState`. Only takes effect once isLoading is false and rows is
  // still empty - a refetch that fails while stale rows are on screen
  // keeps showing those rows rather than blanking them out. See
  // `showError`/`showEmpty` below.
  isError = false,
  errorState,
  className = "",
  toolbarLeft,
  toolbarRight,
  // Row visual spacing (48/60/72px) - unrelated to useDensityScale, which is
  // a DPR/zoom compensation factor read by StatusTag/TableRenderers, not a
  // row-height setting. No UI control wired to this yet; callers can opt a
  // page into "compact"/"roomy" once there's a reason to.
  density = "comfortable",
}) => {
  const [sorting, setSorting] = useState([]);
  const rows = data ?? [];

  const tbodyRef = useRef(null);
  const [headerMinSizes, measureRef] = useHeaderMinSizes(columns);
  const contentMinSizes = useContentMinSizes(tbodyRef, rows, isLoading);
  const resolvedColumns = useMemo(
    () =>
      columns.map((column) => {
        const id = column.id ?? column.accessorKey;
        const headerFloor = headerMinSizes[id] ?? 0;
        const contentFloor = contentMinSizes[id] ?? 0;
        const measuredWidth = Math.max(headerFloor, contentFloor, DEFAULT_MIN_SIZE);
        return {
          ...column,
          // A columnDef with no authored `size` auto-fits to its own real
          // header label + widest current-page value instead of falling
          // back to TanStack's flat 150px default - the exact same
          // measurement that already floors a manually-sized column below
          // is just as good at choosing a sensible starting width when
          // nobody hand-picked one. An authored `size` still wins as the
          // starting width (this floor still guards it from going narrower
          // than its own content needs).
          size: column.size ?? measuredWidth,
          minSize: Math.max(column.minSize ?? DEFAULT_MIN_SIZE, headerFloor, contentFloor),
        };
      }),
    [columns, headerMinSizes, contentMinSizes]
  );

  const table = useReactTable({
    data: rows,
    columns: resolvedColumns,
    state: { pagination, sorting },
    onPaginationChange,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    rowCount: totalCount,
    columnResizeMode: "onChange",
    enableColumnResizing: true,
  });

  const { columnSizingInfo, columnSizing } = table.getState();
  const columnSizeVars = useMemo(() => {
    const headers = table.getFlatHeaders();
    const sizes = {};
    for (const header of headers) {
      sizes[`--header-${header.id}-size`] = header.getSize();
      sizes[`--col-${header.column.id}-size`] = header.column.getSize();
    }
    return sizes;
    // Keyed on columnSizingInfo/columnSizing (per TanStack's
    // performant-resizing guide), not `table` itself - `table` is a new
    // object every render, so depending on it would recompute every render
    // and defeat the point of memoizing this. `resolvedColumns` IS safe to
    // add - it's its own useMemo, so it's referentially stable except when
    // headerMinSizes/contentMinSizes actually change - and it has to be
    // here: those two floors are what make a column auto-widen past its
    // declared `size` to fit a long header/value, and without this
    // dependency that widening only ever reached the screen after a user
    // manually dragged a resize handle (which touches columnSizing and so
    // happened to force a recompute) - a fresh page load stayed clipped at
    // the column's original `size` forever, even though the floor was
    // already computed correctly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnSizingInfo, columnSizing, resolvedColumns]);

  const showError = !isLoading && isError && rows.length === 0;
  const showEmpty = !isLoading && !showError && rows.length === 0;
  const rangeStart = totalCount === 0 ? 0 : pagination.pageIndex * pagination.pageSize + 1;
  const rangeEnd = Math.min((pagination.pageIndex + 1) * pagination.pageSize, totalCount);
  const isResizingColumn = Boolean(table.getState().columnSizingInfo.isResizingColumn);

  return (
    <div
      className={`ui-table ${showEmpty || showError ? "ui-table--empty" : ""} ${
        showError ? "ui-table--error" : ""
      } ${className}`.trim()}
      data-density={density}
    >
      <span
        ref={measureRef}
        className="ui-table__header-cell ui-table__header-content ui-table__measure"
        aria-hidden="true"
      />
      {(toolbarLeft || toolbarRight) && (
        <div className="ui-table__toolbar">
          <div className="ui-table__toolbar-left">
            {toolbarLeft}
            {isLoading && <span className="ui-table__toolbar-spinner" aria-label="Loading" />}
          </div>
          {toolbarRight && <div className="ui-table__toolbar-right">{toolbarRight}</div>}
        </div>
      )}
      <div className="ui-table__scroll">
        <table
          className={`ui-table__grid ${isResizingColumn ? "ui-table__grid--resizing" : ""}`}
          style={{ ...columnSizeVars, width: table.getTotalSize(), minWidth: "100%" }}
        >
          <thead className="ui-table__head">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const isSorted = Boolean(header.column.getIsSorted());
                  const alignRight = header.column.columnDef.meta?.align === "right";
                  return (
                    <th
                      key={header.id}
                      className={`ui-table__header-cell ${canSort ? "ui-table__header-cell--sortable" : ""} ${
                        isSorted ? "ui-table__header-cell--sorted" : ""
                      } ${alignRight ? "ui-table__header-cell--right" : ""}`}
                      style={{ width: `calc(var(--header-${header.id}-size) * 1px)` }}
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    >
                      <span className="ui-table__header-content">
                        <span className="ui-table__header-label">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </span>
                        {canSort && <SortIcon sortState={header.column.getIsSorted()} />}
                      </span>
                      <ColumnResizer header={header} />
                    </th>
                  );
                })}
                {/* Unstyled trailing cell, deliberately with no width of its
                    own - under table-layout:fixed, a table forced to
                    minWidth:100% (below) must give its leftover width to
                    SOME column. Without this, every real column (which all
                    have an explicit px width) gets stretched proportionally
                    instead, pushing short content like "EUR" or "Active"
                    apart with blank space. This absorbs that slack alone,
                    so real columns stay exactly at their declared/resized
                    size regardless of how wide the viewport is. */}
                <th className="ui-table__header-cell ui-table__header-cell--spacer" aria-hidden="true" />
              </tr>
            ))}
          </thead>
          <tbody className="ui-table__body" ref={tbodyRef}>
            {isLoading ? (
              <SkeletonRows columns={columns} rowCount={pagination.pageSize || DEFAULT_PAGE_SIZE} />
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="ui-table__row">
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={`ui-table__cell ${
                        cell.column.columnDef.meta?.align === "right" ? "ui-table__cell--right" : ""
                      }`.trim()}
                      data-column-id={cell.column.id}
                      style={{ width: `calc(var(--col-${cell.column.id}-size) * 1px)` }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                  <td className="ui-table__cell ui-table__cell--spacer" aria-hidden="true" />
                </tr>
              ))
            )}
          </tbody>
        </table>
        {/* Inside the scroll region (not after it) so the horizontal scrollbar sits at the
            bottom of the card, below the message, instead of right under the headings. */}
        {showError && <div className="ui-table__empty ui-table__empty--error">{errorState}</div>}
        {showEmpty && <div className="ui-table__empty">{emptyState}</div>}
      </div>

      {!isLoading && !showError && !showEmpty && totalCount > 0 && (
        <div className="ui-table__pagination">
          <span className="ui-table__pagination-summary">
            Showing {rangeStart}-{rangeEnd} of {totalCount}
          </span>
          <div className="ui-table__pagination-controls">
            <button
              type="button"
              className="ui-table__page-button ui-table__page-button--text"
              disabled={!table.getCanPreviousPage()}
              onClick={() => table.previousPage()}
              aria-label="Previous page"
            >
              Previous
            </button>
            <button
              type="button"
              className="ui-table__page-button ui-table__page-button--text"
              disabled={!table.getCanNextPage()}
              onClick={() => table.nextPage()}
              aria-label="Next page"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Table;
